import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "grid-1" }
const _hoisted_2 = { class: "grid-2-1 w-full items-center" }
const _hoisted_3 = {
  key: 0,
  class: "text-dark-muted text-sm"
}
const _hoisted_4 = { class: "grid-1-2 text-dark-muted space-x-3 text-sm" }
const _hoisted_5 = { class: "flex items-center w-full overflow-hidden" }
const _hoisted_6 = {
  key: 0,
  class: "flex-shrink-0 text-dark-muted text-sm ml-2"
}
const _hoisted_7 = { class: "w-full flex space-x-3 items-center" }
const _hoisted_8 = { class: "w-full flex space-x-3 items-center" }
const _hoisted_9 = { class: "w-full space-x-2 flex justify-end" }
const _hoisted_10 = {
  key: 0,
  class: "text-danger-light text-end text-sm"
}

import { SingleSelect, useSelectServerDataManager } from '@ui-kit/inputs/select';
import { Table, TableComponentParams, TableClientDataManager } from '@ui-kit/table';
import Label from '@ui-kit/label/Label.vue';
import Button from '@ui-kit/controls/button/Button.vue';
import { useI18n } from 'vue-i18n';
import { OrderCreateUpdateDto } from '../dto';
import { ItemResource } from '@shared/resources/item';
import { RepairOrderResource } from '@shared/resources/repair-order';
import { computed, ref, watch } from 'vue';
import SingleLine from '@ui-kit/inputs/single-line/SingleLine.vue';
import ValidationKeyObserver from '@components/validation/ValidationKeyObserver.vue';
import ShowMoreLine from '@ui-kit/show-more/ShowMoreLine.vue';
import { coinsFormat } from '@shared/helpers/data-converters/number-converters';
import { useAutoSalePrice } from '@/helpers/auto-sale-price';
import Mark from '@ui-kit/mark/Mark.vue';
import { countItemPrice } from '@shared/helpers/sum';

interface Props {
  data: RepairOrderResource;
  formData: OrderCreateUpdateDto;
}
interface Emits {
  (type: 'update', event: OrderCreateUpdateDto['items']): void;
}
interface SelectedItem {
  item: ItemResource;
  id: number;
  qty: string;
  price: string;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'TabItems',
  props: {
    data: null,
    formData: null
  },
  emits: ["update"],
  setup(__props: any, { emit }: { emit: ({
  (type: 'update', event: OrderCreateUpdateDto['items']): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  data: RepairOrderResource;
  formData: OrderCreateUpdateDto;
}

const { t } = useI18n();
const generateSalePrice = useAutoSalePrice();





const itemsSelectManager = useSelectServerDataManager({
  path: 'items/grid',
});

function chooseItem(item: ItemResource) {
  const existedItem = selectedItems.value.find(({ id }) => id === item.id);
  if (existedItem) {
    selectedItems.value = selectedItems.value.map((current) => ({
      ...current,
      qty: current.id === item.id ? String((Number(current.qty) || 0) + 1) : current.qty,
    }));
  } else {
    selectedItems.value = [
        {
          item,
          id: item.id,
          price: coinsFormat(generateSalePrice(item)),
          qty: '1',
        },
        ...selectedItems.value,
    ];
  }
}
function removeItem(id: number) {
  selectedItems.value = selectedItems.value.filter((item) => item.id !== id);
}
const selectedItems = ref<SelectedItem[]>((props.data?.items || []).map((item) => ({
  ...item,
  qty: String(item.qty),
  price: String(item.price),
  id: item.item.id,
})));
watch(selectedItems, (value) => {
  emit('update', value.map((item) => {
    return {
      qty: item.qty,
      price: item.price,
      id: item.id,
    };
  }));
});
const itemsTableManager = new TableClientDataManager({
  providedData: computed(() => selectedItems.value.map((item, index) => ({...item, index}))),
  searchBy: ['item.name'],
});
function updateItemPrice(id: number, price: string) {
  selectedItems.value = selectedItems.value.map((current) => ({
    ...current,
    price: current.id === id ? price : current.price,
  }));
}
function updateItemQty(id: number, qty: string) {
  selectedItems.value = selectedItems.value.map((current) => ({
    ...current,
    qty: current.id === id ? qty : current.qty,
  }));
}

function getItemPriceText(item: SelectedItem) {
  return t('pages.orders.stock-price', { price: coinsFormat(generateSalePrice(item.item))});
}
function getQuantityText(item: SelectedItem) {
  const key = Number(item.qty) <= Number(item.item.qty) ? 'stock-qty' : 'stock-qty-limit';
  return t(`pages.orders.${key}`, {qty: item.item.qty});
}
const itemsTableParams = computed<TableComponentParams<SelectedItem>>(() => {
  return {
    searchable: false,
    columns: [
      {
        name: 'item.name',
        data: 'item.name',
        title: t('fields.name'),
        sortable: true,
        responsiveOrder: 1,
      },
      {
        name: 'qty',
        title: t('fields.qty'),
        width: 160,
      },
      {
        name: 'price',
        title: t('fields.price_local'),
        width: 160,
        cellClasses: 'text-gray-600 text-sm',
      },
      {
        name: 'result_price',
        title: t('pages.orders.result-price'),
        data: (row) => {
          return coinsFormat(countItemPrice({
            ...row,
            qty: 1,
          }, {
            adjustment: props.formData.adjustment,
          }));
        },
        width: 160,
        hidden: props.formData.adjustment === 0
      },
      {
        name: 'sum',
        title: t('fields.sum_local'),
        data: (row) => {
          return coinsFormat(countItemPrice(row, {
            adjustment: props.formData.adjustment,
          }));
        },
        width: 120,
        hidden: props.formData.adjustment === 0
      },
      {
        name: 'actions',
        title: '',
        width: 150,
        responsiveOrder: 2,
      },
    ],
  };
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(Label, {
      label: _ctx.$t('pages.orders.select-add-item')
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(SingleSelect), {
          "data-manager": _unref(itemsSelectManager),
          "select-option-text": _ctx.$t('pages.orders.select-item'),
          "onUpdate:modelValue": chooseItem
        }, {
          option: _withCtx(({ option }) => [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", null, [
                (option.category)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_3, "(" + _toDisplayString(option.category.name) + ")", 1))
                  : _createCommentVNode("", true),
                _createTextVNode(" " + _toDisplayString(option.name), 1)
              ]),
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("span", null, _toDisplayString(_ctx.$t('fields.qty')) + ": " + _toDisplayString(option.qty), 1),
                _createElementVNode("span", null, _toDisplayString(_ctx.$t('fields.price_local')) + ": " + _toDisplayString(_unref(coinsFormat)(_unref(generateSalePrice)(option))), 1)
              ])
            ])
          ]),
          _: 1
        }, 8, ["data-manager", "select-option-text"])
      ]),
      _: 1
    }, 8, ["label"]),
    _createVNode(_unref(Table), {
      params: _unref(itemsTableParams),
      "data-manager": _unref(itemsTableManager)
    }, {
      "cell-item.name": _withCtx(({ row }) => [
        _createElementVNode("div", _hoisted_5, [
          _createVNode(ShowMoreLine, null, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(row.item.short_name || row.item.name), 1)
            ]),
            _: 2
          }, 1024),
          (row.item.category)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, " (" + _toDisplayString(row.item.category.name) + ") ", 1))
            : _createCommentVNode("", true)
        ])
      ]),
      "cell-qty": _withCtx(({ row }) => [
        _createElementVNode("div", _hoisted_7, [
          _createVNode(ValidationKeyObserver, {
            keys: ['items', row.index, 'qty']
          }, {
            default: _withCtx(({ firstErrorMessage }) => [
              _createVNode(SingleLine, {
                "model-value": row.qty,
                "onUpdate:modelValue": ($event: any) => (updateItemQty(row.id, $event)),
                type: "number",
                errors: firstErrorMessage,
                flat: "",
                small: "",
                class: "table-input"
              }, null, 8, ["model-value", "onUpdate:modelValue", "errors"])
            ]),
            _: 2
          }, 1032, ["keys"]),
          (Number(row.qty) <= Number(row.item.qty))
            ? (_openBlock(), _createBlock(Mark, {
                key: 0,
                small: "",
                "tooltip-message": getQuantityText(row),
                icon: "question",
                color: "primary"
              }, null, 8, ["tooltip-message"]))
            : (_openBlock(), _createBlock(Mark, {
                key: 1,
                small: "",
                "tooltip-message": getQuantityText(row)
              }, null, 8, ["tooltip-message"]))
        ])
      ]),
      "cell-price": _withCtx(({ row }) => [
        _createElementVNode("div", _hoisted_8, [
          _createVNode(ValidationKeyObserver, {
            keys: ['items', row.index, 'price']
          }, {
            default: _withCtx(({ firstErrorMessage }) => [
              _createVNode(SingleLine, {
                "model-value": row.price,
                "onUpdate:modelValue": ($event: any) => (updateItemPrice(row.id, $event)),
                type: "number",
                errors: firstErrorMessage,
                flat: "",
                small: "",
                class: "table-input"
              }, null, 8, ["model-value", "onUpdate:modelValue", "errors"])
            ]),
            _: 2
          }, 1032, ["keys"]),
          (_unref(coinsFormat)(_unref(generateSalePrice)(row.item)) !== _unref(coinsFormat)(row.price))
            ? (_openBlock(), _createBlock(Mark, {
                key: 0,
                small: "",
                "tooltip-message": getItemPriceText(row),
                icon: "question",
                color: "primary"
              }, null, 8, ["tooltip-message"]))
            : _createCommentVNode("", true)
        ])
      ]),
      "cell-actions": _withCtx(({ row }) => [
        _createElementVNode("div", _hoisted_9, [
          _createVNode(Button, {
            onClick: ($event: any) => (removeItem(row.id)),
            color: "danger"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('common.actions.delete')), 1)
            ]),
            _: 2
          }, 1032, ["onClick"])
        ])
      ]),
      _: 1
    }, 8, ["params", "data-manager"]),
    _createVNode(ValidationKeyObserver, { keys: "items" }, {
      default: _withCtx(({ firstErrorMessage }) => [
        firstErrorMessage
          ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(firstErrorMessage) + "! ", 1))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})